import React, { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { RadioGroup, RadioGroupItem } from '@repo/videa-components';
import { Label } from '@repo/videa-components/src/ui/label';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import classNames from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';

import { useProceduresBySpecialty } from '@/react-query/useReferralInfo';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import { type ValidUniversalToothId } from '@videa/utils';
import { combinedUniversalToFDIMap } from '@videa/utils';

import { PrimaryOdontogram } from './PrimaryOdontogram';
import { ToothQuadrant } from './ToothQuadrant';
import { TreatmentSelector } from './TreatmentSelector';

interface SimpleOdontogramProps {
  className?: string;
}

type ToothType = 'permanent' | 'primary';

export const SimpleOdontogram: React.FC<SimpleOdontogramProps> = ({
  className,
}) => {
  const { t } = useLingui();
  const { displayToothNumber } = useUniversalToFDI();
  const { setValue, watch, register } = useFormContext();

  // Watch form values
  const toothSelectionMode = watch('selectionMode') || 'manual';
  const toothType = watch('toothType') || 'permanent';
  const selectedTeeth = new Set<ValidUniversalToothId>(
    watch('selectedTeeth') || [],
  );
  const selectedTreatments = new Set<number>(watch('selectedTreatments') || []);
  const selectedSpecialtyId = useWatch({ name: 'specialtyId' });

  const { data: procedures, isLoading: isProceduresLoading } =
    useProceduresBySpecialty(selectedSpecialtyId || 0, {
      enabled: !!selectedSpecialtyId,
    });

  // Ensure selectedTreatments is always a valid Set
  const safeSelectedTreatments =
    selectedTreatments instanceof Set ? selectedTreatments : new Set<number>();

  const handleToothTypeChange = (type: ToothType) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setValue('toothType', type);
  };

  const handleToothClick = (toothId: ValidUniversalToothId) => {
    const newSelectedTeeth = new Set(selectedTeeth);
    if (newSelectedTeeth.has(toothId)) {
      newSelectedTeeth.delete(toothId);
    } else {
      newSelectedTeeth.add(toothId);
    }
    if (toothSelectionMode !== 'manual') {
      setValue('selectionMode', 'manual');
    }
    setValue('selectedTeeth', Array.from(newSelectedTeeth));
  };

  const handleTreatmentToggle = (treatmentId: number) => {
    const newSelectedTreatments = new Set(selectedTreatments);
    if (newSelectedTreatments.has(treatmentId)) {
      newSelectedTreatments.delete(treatmentId);
    } else {
      newSelectedTreatments.add(treatmentId);
    }
    setValue('selectedTreatments', Array.from(newSelectedTreatments));
  };

  const handleToothSelectionModeChange = (
    value: 'manual' | 'selectAll' | 'skipSelection',
  ) => {
    setValue('selectionMode', value);
    if (value === 'selectAll') {
      const allTeeth = Object.keys(combinedUniversalToFDIMap);
      setValue('selectedTeeth', allTeeth as ValidUniversalToothId[]);
    } else if (value === 'skipSelection') {
      setValue('selectedTeeth', []);
    } else if (value === 'manual') {
      setValue('selectedTeeth', undefined);
    }
  };

  return (
    <div
      className="vai-flex vai-flex-col vai-gap-4"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <div className="vai-flex vai-justify-center vai-gap-2">
        <button
          onClick={handleToothTypeChange('permanent')}
          className={classNames(
            'vai-px-4 vai-py-2 vai-rounded vai-text-sm',
            toothType === 'permanent'
              ? 'vai-bg-blue-600 vai-text-white'
              : 'vai-bg-gray-200 vai-text-gray-700',
          )}
        >
          <Trans>Permanent</Trans>
        </button>
        <button
          onClick={handleToothTypeChange('primary')}
          className={classNames(
            'vai-px-4 vai-py-2 vai-rounded vai-text-sm',
            toothType === 'primary'
              ? 'vai-bg-blue-600 vai-text-white'
              : 'vai-bg-gray-200 vai-text-gray-700',
          )}
        >
          <Trans>Primary</Trans>
        </button>
      </div>

      <div className="vai-flex vai-flex-row vai-justify-center vai-items-center vai-gap-2">
        <RadioGroup
          className="vai-grid-cols-3"
          value={toothSelectionMode}
          onValueChange={handleToothSelectionModeChange}
        >
          <div className="vai-flex vai-flex-row vai-justify-center vai-items-center vai-gap-2">
            <RadioGroupItem value="manual" />
            <Trans>Manually Select</Trans>
          </div>

          <div className="vai-flex vai-flex-row vai-justify-center vai-items-center vai-gap-2">
            <RadioGroupItem value="selectAll" />
            <Trans>Select All Teeth</Trans>
          </div>

          <div className="vai-flex vai-flex-row vai-justify-center vai-items-center vai-gap-2">
            <RadioGroupItem value="skipSelection" />
            <Trans>Skip Tooth Selection</Trans>
          </div>
        </RadioGroup>
      </div>

      {toothSelectionMode !== 'skipSelection' &&
        (toothType === 'permanent' ? (
          <div
            className={classNames(
              'vai-flex vai-justify-center vai-items-center vai-h-fit',
              className,
            )}
          >
            <div className="vai-grid vai-grid-cols-2 vai-grid-rows-2 vai-gap-0.5 vai-items-center vai-w-fit vai-h-fit vai-content-center">
              <ToothQuadrant
                startNumber={1}
                position="topRight"
                onToothClick={handleToothClick}
                selectedTooth={selectedTeeth}
              />
              <ToothQuadrant
                startNumber={9}
                position="topLeft"
                onToothClick={handleToothClick}
                selectedTooth={selectedTeeth}
              />
              <ToothQuadrant
                startNumber={32}
                position="bottomLeft"
                onToothClick={handleToothClick}
                selectedTooth={selectedTeeth}
              />
              <ToothQuadrant
                startNumber={24}
                position="bottomRight"
                onToothClick={handleToothClick}
                selectedTooth={selectedTeeth}
              />
            </div>
          </div>
        ) : (
          <PrimaryOdontogram
            className={className}
            onToothClick={handleToothClick}
            selectedTooth={selectedTeeth}
          />
        ))}

      {selectedTeeth.size > 0 && toothSelectionMode == 'manual' && (
        <div className="vai-flex vai-flex-wrap vai-gap-2 vai-justify-center vai-p-2 vai-bg-gray-50 vai-rounded">
          {Array.from(selectedTeeth).map((toothId) => (
            <div
              key={toothId}
              className="vai-flex vai-items-center vai-gap-1 vai-bg-blue-100 vai-px-3 vai-py-1 vai-rounded-full vai-text-blue-800"
            >
              <span className="vai-text-sm vai-font-medium">
                {displayToothNumber(toothId)}
              </span>
              <button
                onClick={() => handleToothClick(toothId)}
                className="vai-text-blue-600 hover:vai-text-blue-800 vai-font-medium vai-text-sm"
              >
                ×
              </button>
            </div>
          ))}{' '}
        </div>
      )}

      {toothSelectionMode == 'selectAll' && (
        <div className="vai-flex vai-flex-wrap vai-gap-2 vai-justify-center vai-p-2 vai-bg-gray-50 vai-rounded">
          <div className="vai-flex vai-items-center vai-gap-1 vai-bg-blue-100 vai-px-3 vai-py-1 vai-rounded-full vai-text-blue-800">
            <span className="vai-text-sm vai-font-medium">
              <Trans>All Teeth</Trans>
            </span>
            <button
              onClick={() => handleToothSelectionModeChange('manual')}
              className="vai-text-blue-600 hover:vai-text-blue-800 vai-font-medium vai-text-sm"
            >
              x
            </button>
          </div>
        </div>
      )}

      <TreatmentSelector
        treatments={procedures || []}
        selectedTreatments={safeSelectedTreatments}
        onTreatmentToggle={handleTreatmentToggle}
        isLoading={isProceduresLoading}
        required
      />

      <div className="vai-space-y-2">
        <Label htmlFor="narrative" className="vai-text-sm">
          <Trans>Treatment Notes</Trans>
        </Label>
        <Textarea
          id="narrative"
          placeholder={t`Write or paste your treatment notes here...`}
          className="vai-text-sm vai-resize-none vai-h-24"
          {...register('narrative')}
        />
      </div>
    </div>
  );
};
